import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TabViewTab } from './tab-view.model';
import { buildRouteCacheKey } from './tab-view-route-key';

const TAB_VIEW_ENABLED_STORAGE_KEY = 'anessa-tab-view-enabled';
/** The open tab list, active tab and LRU order, so a reload brings the tabs back. */
const TAB_VIEW_STATE_STORAGE_KEY = 'anessa-tab-view-state';

interface PersistedTabViewState {
  tabs: TabViewTab[];
  activeTabId: string | null;
  lru: string[];
}

/**
 * Owns the experimental tab-view state (see the `~tabs` dev command):
 * which tabs are open, which is active, and the LRU cap that keeps the
 * `TabViewRouteReuseStrategy` cache from growing unbounded. The `enabled`
 * toggle and the open tab list survive a reload (via localStorage). Restored
 * tabs are lazy: only the page the reload lands on is rendered, and every
 * other tab's page loads the first time it is activated — the reuse cache
 * only ever holds tabs that have been visited.
 *
 * The saved state is one localStorage entry shared by every browser window:
 * each window writes its whole state, so with two open the last writer wins
 * and reloading one can bring back the other's tabs. That's accepted for a
 * dev-only feature; real per-window state would need a `storage` event
 * listener (or sessionStorage).
 *
 * Deliberately has no dependency on TabViewRouteReuseStrategy — the strategy
 * reads `tabs()` reactively to know when to destroy a closed/evicted tab's
 * cached component, so this service only ever mutates plain signals.
 */
@Injectable({ providedIn: 'root' })
export class TabViewService {
  static readonly MAX_TABS = 8;

  // Router itself depends on RouteReuseStrategy (TabViewRouteReuseStrategy),
  // which depends on this service — injecting Router eagerly here would close
  // that loop (NG0200). Injector defers the lookup until a tab is actually
  // opened/switched, long after Router has finished constructing.
  private readonly injector = inject(Injector);
  private get router(): Router {
    return this.injector.get(Router);
  }

  readonly enabled = signal(this.loadEnabled());
  private readonly restored = this.enabled() ? this.loadState() : null;
  readonly tabs = signal<TabViewTab[]>(this.restored?.tabs ?? []);
  readonly activeTabId = signal<string | null>(
    this.restored?.activeTabId ?? null,
  );

  readonly activeTab = computed(
    () => this.tabs().find((tab) => tab.id === this.activeTabId()) ?? null,
  );

  /** Titles a tab after its page moves within it — see {@link setTitleResolver}. */
  private titleResolver: ((url: string) => string | null) | null = null;

  /** Most-recently-used tab ids first. Keyed by id, not url — two tabs can
   *  share a url (see TabViewTab.id's doc comment), and a url key would let
   *  closing/touching one silently drop or resurrect the other's entry. */
  private readonly lru: string[] = this.restored?.lru ?? [];

  constructor() {
    // Re-saved whenever the tab list or the active tab changes; the LRU order
    // moves together with those, so it is saved alongside.
    effect(() => {
      this.saveState({
        tabs: this.tabs(),
        activeTabId: this.activeTabId(),
        lru: [...this.lru],
      });
    });
    // Deferred like the `router` getter: this runs while Router is still
    // being constructed.
    queueMicrotask(() => this.followNavigation());
  }

  /**
   * After every navigation the active tab is the one whose url is the landed
   * page, or none — so the tab bar and Ctrl+Alt+W never act on a tab that
   * isn't showing. A plain click elsewhere (sidebar, a link) leaves the tab
   * open but unselected, and coming back to its page selects it again. This
   * also covers a reload, whose saved active tab may not be the page it opens
   * (a deep link in a new window, or moving around inside a tab before
   * reloading).
   *
   * A navigation that stays within the active tab (see `staysInTab`) keeps it
   * selected and moves its url to the landed page instead, so the tab
   * reopens where the user left it.
   */
  private followNavigation(): void {
    const router = this.router;
    if (router.navigated) this.activateTabForCurrentUrl();
    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.activateTabForCurrentUrl(e.url));
  }

  /** `requestedUrl` is what the navigation asked for, before any redirect. */
  private activateTabForCurrentUrl(requestedUrl?: string): void {
    const page = landedPage(this.router.routerState.snapshot.root);
    const urlKey = buildRouteCacheKey(page);
    // Two tabs can share a url: keep the active one when it's on that url, so a
    // same-url reload (e.g. a plant switch) doesn't jump to its first twin.
    const active = this.activeTab();
    const tab =
      active?.url === urlKey
        ? active
        : (this.tabs().find((t) => t.url === urlKey) ?? null);
    if (tab) {
      this.touch(tab.id);
      this.activeTabId.set(tab.id);
      return;
    }
    if (active && staysInTab(active.url, urlKey, requestedUrl, itemListOf(page))) {
      // A tab that moved to another page takes that page's title (a
      // dashboard's or custom view's name); a query-only move keeps its own.
      const moved = pathOf(active.url) !== pathOf(urlKey);
      const title = moved ? this.movedTabTitle(active, urlKey) : active.title;
      this.tabs.update((tabs) =>
        tabs.map((t) => (t.id === active.id ? { ...t, url: urlKey, title } : t)),
      );
      this.touch(active.id);
      return;
    }
    this.activeTabId.set(null);
  }

  /**
   * A moved tab's new title: the resolver's, else its own — unless that was
   * only its old url (the fallback for a page nothing can name, e.g.
   * `/audit/x`), which would now name the wrong page, so it takes the new url.
   */
  private movedTabTitle(tab: TabViewTab, urlKey: string): string {
    const resolved = this.titleResolver?.(urlKey);
    if (resolved) return resolved;
    const titledByUrl = tab.title === tab.url || tab.title === pathOf(tab.url);
    return titledByUrl ? urlKey : tab.title;
  }

  /**
   * How a tab that moved to another page within itself is retitled: the
   * layout's resolver, which knows page labels and dashboard / custom view
   * names. Null keeps the tab's title.
   */
  setTitleResolver(resolver: (url: string) => string | null): void {
    this.titleResolver = resolver;
  }

  toggle(): void {
    this.enabled.update((v) => !v);
    this.saveEnabled(this.enabled());
    if (!this.enabled()) this.reset();
  }

  private loadEnabled(): boolean {
    try {
      return localStorage.getItem(TAB_VIEW_ENABLED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private saveEnabled(value: boolean): void {
    try {
      localStorage.setItem(TAB_VIEW_ENABLED_STORAGE_KEY, String(value));
    } catch {
      // Best-effort — private browsing / storage quota shouldn't break toggling.
    }
  }

  private loadState(): PersistedTabViewState | null {
    try {
      const raw = localStorage.getItem(TAB_VIEW_STATE_STORAGE_KEY);
      return raw ? parsePersistedState(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private saveState(state: PersistedTabViewState): void {
    try {
      localStorage.setItem(TAB_VIEW_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Best-effort, like saveEnabled.
    }
  }

  isOpenTabUrl(urlKey: string): boolean {
    return this.tabs().some((tab) => tab.url === urlKey);
  }

  /**
   * Opens `url` as a new tab. `background` adds it to the tab bar without
   * switching to it — the browser's Ctrl+click behavior — so whatever is on
   * screen (e.g. the command palette) stays put.
   */
  openTab(url: string, title: string, { background = false }: { background?: boolean } = {}): void {
    const tab: TabViewTab = { id: crypto.randomUUID(), url, title };
    this.tabs.update((tabs) => [...tabs, tab]);
    this.touch(tab.id);
    if (!background) {
      this.activeTabId.set(tab.id);
      void this.router.navigateByUrl(url);
    }
    this.evictIfOverCapacity();
  }

  /**
   * Replaces every open tab with `entries`, in order, and switches to the
   * first — the `~usetabs` dev command. Turns tab view on if it's off, since a
   * tab list means nothing without it. Entries that aren't in-app paths are
   * skipped and the list is capped at MAX_TABS, the same rules a restored
   * state goes through.
   */
  replaceTabs(entries: { url: string; title: string }[]): void {
    const tabs: TabViewTab[] = entries
      .filter((entry) => IN_APP_PATH.test(entry.url))
      .slice(0, TabViewService.MAX_TABS)
      .map(({ url, title }) => ({ id: crypto.randomUUID(), url, title }));
    const [first] = tabs;
    if (!first) return;

    if (!this.enabled()) this.toggle();
    this.tabs.set(tabs);
    // First tab most recently used, the rest in list order after it.
    this.lru.length = 0;
    this.lru.push(...tabs.map((tab) => tab.id));
    this.activeTabId.set(first.id);
    void this.router.navigateByUrl(first.url);
  }

  /**
   * Keeps the page on screen as a new, active tab — the Ctrl+Alt+P shortcut
   * for a page reached by ordinary navigation rather than through a tab. No
   * navigation happens: the page is already rendered, and the reuse strategy
   * keeps it alive from the next time it's left, since its url is now an open
   * tab. Does nothing when tab view is off or the page is already a tab.
   * Returns whether a tab was added.
   */
  tabifyCurrentPage(title: (url: string) => string): boolean {
    if (!this.enabled()) return false;
    const url = buildRouteCacheKey(landedPage(this.router.routerState.snapshot.root));
    if (this.isOpenTabUrl(url)) return false;
    const tab: TabViewTab = { id: crypto.randomUUID(), url, title: title(url) };
    this.tabs.update((tabs) => [...tabs, tab]);
    this.touch(tab.id);
    this.activeTabId.set(tab.id);
    this.evictIfOverCapacity();
    return true;
  }

  /** Switches to an already-open tab. Only commits `activeTabId` if the navigation actually succeeds. */
  activateTab(tabId: string): void {
    const tab = this.tabs().find((t) => t.id === tabId);
    if (!tab) return;
    this.touch(tabId);
    void this.router.navigateByUrl(tab.url).then((navigated) => {
      if (navigated) this.activeTabId.set(tabId);
    });
  }

  /** Reorders the open tab list in place, e.g. after a drag-and-drop in the tab bar. */
  reorderTabs(previousIndex: number, currentIndex: number): void {
    if (previousIndex === currentIndex) return;
    const tabs = [...this.tabs()];
    const [moved] = tabs.splice(previousIndex, 1);
    tabs.splice(currentIndex, 0, moved);
    this.tabs.set(tabs);
  }

  closeTab(tabId: string): void {
    const tab = this.tabs().find((t) => t.id === tabId);
    if (!tab) return;

    const remaining = this.tabs().filter((t) => t.id !== tabId);
    this.tabs.set(remaining);
    this.forgetTab(tabId);

    if (this.activeTabId() !== tabId) return;

    const next = remaining.at(-1) ?? null;
    if (next) {
      this.activateTab(next.id);
    } else {
      this.activeTabId.set(null);
    }
  }

  private touch(tabId: string): void {
    const index = this.lru.indexOf(tabId);
    if (index !== -1) this.lru.splice(index, 1);
    this.lru.unshift(tabId);
  }

  private forgetTab(tabId: string): void {
    const index = this.lru.indexOf(tabId);
    if (index !== -1) this.lru.splice(index, 1);
  }

  /**
   * Closes the least recently used tab once over capacity — never the active
   * one, though. A background open (`openTab` with `background`) touches the
   * new tab without activating it, so the visible tab can be the LRU-oldest;
   * evicting it would navigate the page out from under the user.
   */
  private evictIfOverCapacity(): void {
    if (this.tabs().length <= TabViewService.MAX_TABS) return;
    const activeId = this.activeTabId();
    const oldestId = [...this.lru].reverse().find((id) => id !== activeId);
    if (oldestId) this.closeTab(oldestId);
  }

  private reset(): void {
    this.tabs.set([]);
    this.activeTabId.set(null);
    this.lru.length = 0;
  }
}

/** In-app paths only: a leading `/` that isn't the `//host` of a protocol-relative url. */
const IN_APP_PATH = /^\/(?!\/)/;

/** A url's path, without its query string or fragment. */
function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

/**
 * Whether a navigation that landed on `landedKey` stayed within the tab on
 * `tabUrl`, rather than leaving it for another page:
 *  - only the query string changed — a list's filters, sort or page
 *    (`updateUrlFilters` on Réceptions / Expéditions);
 *  - it asked for the tab's own page and a redirect moved it on (a
 *    `redirectTo` child, `urlIdGuard` sending a stale id back to its list);
 *  - it went deeper below the tab's page — a landing list opening one of its
 *    items (`/dashboard` → `/dashboard/:id`, `/overview` → `/overview/:id`).
 *    A sidebar click to such a page counts too, as it would in a browser tab;
 *  - it went from one item of a list to another (`/dashboard/a` →
 *    `/dashboard/b`, `/custom-views/x` → `/custom-views/y`): `itemList` is
 *    the list the landed item page belongs to, when it is one. Only the
 *    list's items count, not its fixed pages (`/dashboard/operations-site/energie`).
 */
function staysInTab(
  tabUrl: string,
  landedKey: string,
  requestedUrl: string | undefined,
  itemList: ItemList | null,
): boolean {
  const tabPath = pathOf(tabUrl);
  const landedPath = pathOf(landedKey);
  return (
    landedPath === tabPath ||
    (requestedUrl !== undefined && pathOf(requestedUrl) === tabPath) ||
    landedPath.startsWith(`${tabPath}/`) ||
    (itemList !== null && isItemOf(tabPath, itemList))
  );
}

/** The list an item page belongs to, as {@link itemListOf} finds it. */
interface ItemList {
  /** The list's path — `/dashboard` for `/dashboard/:id`. */
  path: string;
  /** How many url segments an item adds to it — 1 for `:id`. */
  itemSegments: number;
  /** The list's fixed pages, relative to it (`operations-site/biodec`). */
  fixedPages: Set<string>;
}

/** Whether `path` is an item of `list`: as deep as its items, and none of its fixed pages. */
function isItemOf(path: string, list: ItemList): boolean {
  if (!path.startsWith(`${list.path}/`)) return false;
  const rest = path.slice(list.path.length + 1);
  return rest.split('/').length === list.itemSegments && !list.fixedPages.has(rest);
}

/** The deepest route of the page a navigation landed on. */
function landedPage(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let route = root;
  while (route.firstChild) route = route.firstChild;
  return route;
}

/**
 * The list an item page belongs to — `/dashboard` for `/dashboard/:id` — when
 * the page's route is nothing but parameters; null for any other page, so two
 * different pages that merely share a parent (`/parametrage/a`,
 * `/parametrage/b`) never count as one tab. The list's fixed pages are the
 * parameter-free routes beside the item route.
 */
function itemListOf(page: ActivatedRouteSnapshot): ItemList | null {
  const configPath = page.routeConfig?.path;
  if (!configPath || !configPath.split('/').every((segment) => segment.startsWith(':'))) return null;
  const segments = page.pathFromRoot.flatMap((r) => r.url.map((segment) => segment.path)).filter(Boolean);
  const parent = segments.slice(0, segments.length - page.url.length);
  if (!parent.length) return null;
  const siblings = page.parent?.routeConfig?.children ?? [];
  const fixedPages = siblings
    .map((route) => route.path)
    .filter((path): path is string => !!path && !path.split('/').some((segment) => segment.startsWith(':')));
  return { path: `/${parent.join('/')}`, itemSegments: page.url.length, fixedPages: new Set(fixedPages) };
}

function isTab(value: unknown): value is TabViewTab {
  const tab = value as Partial<TabViewTab> | null;
  return (
    typeof tab?.id === 'string' &&
    typeof tab.url === 'string' &&
    IN_APP_PATH.test(tab.url) &&
    typeof tab.title === 'string'
  );
}

/**
 * Validates what localStorage held — it may be stale, from an older build, or
 * hand-edited. Bad tabs are dropped, the list is capped at MAX_TABS, and the
 * active id and LRU order are kept only for tabs that survived.
 */
function parsePersistedState(value: unknown): PersistedTabViewState | null {
  const state = value as Partial<PersistedTabViewState> | null;
  if (!Array.isArray(state?.tabs)) return null;
  const tabs = state.tabs.filter(isTab).slice(0, TabViewService.MAX_TABS);
  const ids = new Set(tabs.map((tab) => tab.id));
  const storedLru = Array.isArray(state.lru) ? state.lru : [];
  const lru = storedLru.filter(
    (id): id is string => typeof id === 'string' && ids.has(id),
  );
  // Tabs missing from the stored order count as least recently used.
  for (const tab of tabs) if (!lru.includes(tab.id)) lru.push(tab.id);
  const activeTabId =
    typeof state.activeTabId === 'string' && ids.has(state.activeTabId)
      ? state.activeTabId
      : null;
  return { tabs, activeTabId, lru };
}
